import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ReactComponent as ShieldIcon } from '../../assets/shield icon.svg';
import { FaBookOpen, FaClock, FaHistory, FaChevronRight, FaSearch, FaTrophy, FaArrowRight } from "react-icons/fa";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import "react-circular-progressbar/dist/styles.css";

const TopicSelectionPage = ({ subject }) => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [progress, setProgress] = useState({ earned: 0, total: 0 });
    const [badgeStatus, setBadgeStatus] = useState({});
    const [testHistory, setTestHistory] = useState([]);
    const [studentId, setStudentId] = useState(null);
    const [subjectsStatus, setSubjectsStatus] = useState([]);
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));
    const childId = user?.id || "";

    useEffect(() => {
        const fetchData = async () => {
            const userData = localStorage.getItem("user");
            if (userData) {
                const user = JSON.parse(userData);
                setStudentId(user.id);

                try {
                    // Fetch subject access status
                    const subjectResponse = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/student-access-status/${user.id}`
                    );
                    const subjectData = await subjectResponse.json();
                    setSubjectsStatus(subjectData);

                    // Fetch test history - EXCLUDE MOCK TESTS
                    const historyResponse = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
                    );
                    const historyData = await historyResponse.json();

                    // Only show topic tests (exclude mock and premium tests)
                    const subjectHistory = historyData.filter(test =>
                        test.testType &&
                        test.testType.toLowerCase() !== 'mock' &&
                        !['practice', 'weekly', 'mega'].includes(test.testType.toLowerCase())
                    );
                    setTestHistory(subjectHistory);
                } catch (error) {
                    console.error("Error fetching data:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, []);

    const getTestDisplayName = (test) => {
        if (test.category) {
            return `${test.category} Test`;
        }
        if (test.subject && test.testType) {
            return `${test.subject} ${test.testType.charAt(0).toUpperCase() + test.testType.slice(1)} Test`;
        }
        return 'Topic Test';
    };

    useEffect(() => {
        const fetchData = async () => {
            const userData = localStorage.getItem("user");
            if (userData) {
                const user = JSON.parse(userData);
                const questionType = user.is_oc ? "OC" : "Selective";
                try {
                    // Fetch topics
                    const topicsResponse = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/get-maths-topics`,
                        { params: { questionType } }
                    );
                    setTopics(topicsResponse.data);

                    // Fetch progress data
                    const progressResponse = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/get-badge-count`,
                        { params: { childId, questionType } }
                    );
                    setProgress({
                        earned: progressResponse.data.count || 0,
                        total: progressResponse.data.total || topicsResponse.data.length
                    });

                    // Pre-fetch badge status for all topics
                    const badgeStatusMap = {};
                    for (const topic of topicsResponse.data) {
                        try {
                            const response = await axios.get(
                                `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/check-badge`,
                                { params: { childId, category: topic } }
                            );
                            badgeStatusMap[topic] = response.data.hasBadge;
                        } catch (error) {
                            console.error(`Error checking badge for ${topic}:`, error);
                            badgeStatusMap[topic] = false;
                        }
                    }
                    setBadgeStatus(badgeStatusMap);

                    setLoading(false);
                } catch (error) {
                    console.error("Error fetching data:", error);
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [childId]);

    const handleTopicClick = (topic) => {
        navigate(`/topic-test/${encodeURIComponent(topic)}`);
    };

    const filteredTopics = topics.filter(topic =>
        topic.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatTestDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
                <div className="flex space-x-3">
                    {[...Array(5)].map((_, i) => (
                        <div
                            key={i}
                            className="h-8 w-8 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 animate-bounce"
                            style={{ animationDelay: `${i * 0.1}s` }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-6 md:p-10">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-10 text-center md:text-left">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800 mb-3">
                        <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
                            Master Math Topics
                        </span>
                    </h1>
                    <p className="text-lg text-gray-600 mb-6">
                        Select a topic to begin your learning journey
                    </p>

                    {/* Search Bar */}
                    <div className="relative max-w-xl">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <FaSearch className="text-purple-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search topics..."
                            className="w-full pl-12 pr-5 py-3.5 rounded-xl border-0 ring-2 ring-purple-200 focus:ring-2 focus:ring-purple-500 focus:outline-none shadow-sm text-gray-700"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-purple-400 hover:text-purple-600"
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>

                {/* Progress Card - Unique Design */}
                <div className="mb-12 bg-gradient-to-r from-teal-600 to-blue-500 rounded-2xl p-6 shadow-lg text-white relative overflow-hidden">
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-white bg-opacity-10 rounded-full"></div>
                    <div className="absolute -right-20 -bottom-20 w-60 h-60 bg-white bg-opacity-5 rounded-full"></div>

                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-bold">Your Mastery Progress</h3>
                            <div className="bg-white bg-opacity-20 p-2 rounded-full">
                                <FaTrophy className="text-yellow-300 text-xl" />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm opacity-80">Topics Completed</p>
                                <p className="text-3xl font-bold">
                                    {progress.earned}
                                    <span className="text-xl opacity-70">/{progress.total}</span>
                                </p>
                            </div>

                            {/* Circular Progress (Moon Phase style) */}
                            <div style={{ width: 80, height: 80 }}>
                                <CircularProgressbar
                                    value={(progress.earned / progress.total) * 100}
                                    text={`${Math.round((progress.earned / progress.total) * 100)}%`}
                                    styles={buildStyles({
                                        textSize: "20px",
                                        pathColor: "#FACC15", // yellow (like Authenticator timer)
                                        textColor: "#fff",
                                        trailColor: "rgba(255,255,255,0.2)",
                                    })}
                                />
                            </div>
                        </div>

                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-500 group hover:-translate-y-1">
                        <div className="flex items-center">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 text-purple-600 mr-4 group-hover:rotate-6 transition-transform">
                                <FaBookOpen className="text-xl" />
                            </div>
                            <div>
                                <h3 className="text-gray-500 text-sm font-medium">Total Topics</h3>
                                <p className="text-2xl font-bold text-gray-800">{progress.total}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-blue-500 group hover:-translate-y-1">
                        <div className="flex items-center">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 text-blue-600 mr-4 group-hover:rotate-6 transition-transform">
                                <FaClock className="text-xl" />
                            </div>
                            <div>
                                <h3 className="text-gray-500 text-sm font-medium">Avg. Time</h3>
                                <p className="text-2xl font-bold text-gray-800">15 min</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Topics Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredTopics.length > 0 ? (
                        filteredTopics.map((topic) => {
                            const hasBadge = badgeStatus[topic] || false;

                            return (
                                <div
                                    key={topic}
                                    className={`bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col group overflow-hidden relative ${hasBadge ? "border-2 border-yellow-400" : ""
                                        }`}
                                    onClick={() => handleTopicClick(topic)}
                                >
                                    {hasBadge && (
                                        <div className="absolute top-2 right-0 bg-yellow-400 text-white px-2 py-1 rounded-bl-lg text-xs font-bold flex items-center">
                                            <FaTrophy className="mr-1" /> Mastered
                                        </div>
                                    )}
                                    <div className="relative h-2 bg-gradient-to-r from-orange-500 to-yellow-500"></div>
                                    <div className="p-6 flex flex-col h-full">
                                        <div className="flex items-center mb-4">
                                            <div className={`p-3 rounded-xl mr-4 ${hasBadge
                                                ? "bg-gradient-to-br from-yellow-100 to-yellow-50 text-yellow-600"
                                                : "bg-gradient-to-br from-purple-100 to-blue-100 text-purple-600"
                                                }`}>
                                                <ShieldIcon className="w-6 h-6" />
                                            </div>
                                            <h3 className={`text-lg font-bold line-clamp-2 ${hasBadge ? "text-yellow-700" : "text-gray-800 group-hover:text-purple-600"
                                                }`}>
                                                {topic}
                                            </h3>
                                        </div>
                                        <div className="mt-auto">
                                            <div className="flex justify-between items-center">
                                                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${hasBadge
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : "bg-purple-100 text-purple-600"
                                                    }`}>
                                                    {hasBadge ? "Completed" : "Start Learning"}
                                                </span>
                                                <button className={`flex items-center text-sm font-medium ${hasBadge ? "text-yellow-600 hover:text-yellow-800" : "text-teal-600 hover:text-teal-800"
                                                    }`}>
                                                    {hasBadge ? "Review" : "Begin"} <FaArrowRight className="ml-1" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full text-center py-16">
                            <div className="inline-block p-6 mb-6 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 text-purple-500">
                                <FaSearch className="text-3xl" />
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No topics found</h3>
                            <p className="text-gray-500 max-w-md mx-auto">We couldn't find any topics matching "{searchTerm}"</p>
                            <button
                                onClick={() => setSearchTerm("")}
                                className="mt-6 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-medium rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all shadow-md"
                            >
                                Clear Search
                            </button>
                        </div>
                    )}
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 mt-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FaHistory className="mr-2 text-blue-500" /> Your Topic Test History
                        </h3>
                        <button
                            onClick={() => navigate('/test-history', { state: { filter: 'subjects' } })}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            View All <FaChevronRight className="inline ml-1" />
                        </button>
                    </div>

                    {testHistory.length > 0 ? (
                        <div className="space-y-4">
                            {testHistory.slice(0, 3).map((test, index) => (
                                <div
                                    key={index}
                                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                                    onClick={() => navigate(`/test-results/${test.testType}/${test.testId}`)}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-800">
                                                {getTestDisplayName(test)}
                                            </h4>
                                            <p className="text-sm text-gray-500">{formatTestDate(test.completedAt)}</p>
                                            {test.correctAnswers !== null && test.incorrectAnswers !== null && (
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {test.correctAnswers} correct • {test.incorrectAnswers} incorrect
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center ml-4">
                                            <div style={{ width: 50, height: 50, marginRight: 12 }}>
                                                <CircularProgressbar
                                                    value={test.scorePercentage || 0}
                                                    text={`${Math.round(test.scorePercentage || 0)}%`}
                                                    styles={buildStyles({
                                                        textSize: '28px',
                                                        pathColor: test.scorePercentage >= 70 ? '#10B981' :
                                                            test.scorePercentage >= 40 ? '#F59E0B' : '#EF4444',
                                                        textColor: test.scorePercentage >= 70 ? '#10B981' :
                                                            test.scorePercentage >= 40 ? '#F59E0B' : '#EF4444',
                                                        trailColor: '#E5E7EB',
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="inline-block p-4 rounded-full bg-gray-100 mb-4">
                                <FaHistory className="text-3xl text-gray-400" />
                            </div>
                            <p className="text-gray-500">No topic test history available</p>
                            <p className="text-gray-400 text-sm mt-2">Complete your first topic test to see your history here</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TopicSelectionPage;